import { sharedSheet } from "../styles/sharedStyles.js";
import componentStylesText from "./ClickableBlockComponent.css?inline";

const componentSheet = new CSSStyleSheet();
componentSheet.replaceSync(componentStylesText);

/**
 * ClickableBlockComponent — Shared wrapper for clickable content blocks.
 *
 * Purpose:
 * - Provides a consistent clickable button wrapper with shared styles.
 * - Appends an enlarge icon container on the right.
 * - Moves any child nodes into a dedicated content container inside the button.
 *
 * Usage:
 * ```html
 * <jt-clickable-block>
 *   <!-- Your content nodes here -->
 * </jt-clickable-block>
 * ```
 *
 * Notes:
 * - This component is intentionally dumb: no custom events or domain logic.
 * - Consumers (e.g., TestimonialBlockComponent) attach their own click handlers
 *   on the wrapper element to dispatch domain-specific events.
 */
export class ClickableBlockComponent extends HTMLElement {
    private iconElement: HTMLElement | null = null;

    static get observedAttributes(): string[] {
        return ["icon"];
    }

    constructor() {
        super();
    }

    connectedCallback(): void {
        if (!this.shadowRoot) {
            this.render();
        }
        this.updateIcon();
    }

    attributeChangedCallback(name: string, _oldValue: string | null, _newValue: string | null): void {
        if (name === "icon") {
            this.updateIcon();
        }
    }

    /**
     * Creates the shadow DOM with a button wrapper, content container, and an icon container.
     */
    private render(): void {
        const shadow = this.attachShadow({ mode: "open" });
        shadow.adoptedStyleSheets = [sharedSheet, componentSheet];

        // Button wrapper with shared classes
        const button = document.createElement("button");
        button.type = "button";
        button.className = "chat-message__bubble clickable-block";

        // Inner content container (consumer-owned DOM will be slotted here)
        const content = document.createElement("div");
        content.className = "clickable-block__content";
        
        const slot = document.createElement("slot");
        content.appendChild(slot);
        button.appendChild(content);

        // Icon container (right-side action icon)
        const iconContainer = document.createElement("div");
        iconContainer.className = "clickable-block__icon-container";

        this.iconElement = document.createElement("jt-icon");
        this.iconElement.className = "clickable-block__icon";
        iconContainer.appendChild(this.iconElement);

        button.appendChild(iconContainer);
        shadow.appendChild(button);
    }

    private updateIcon(): void {
        if (this.iconElement) {
            const icon = this.getAttribute("icon") || "expand";
            this.iconElement.setAttribute("icon", icon);
        }
    }
}

// Register custom element
if (!customElements.get("jt-clickable-block")) {
    customElements.define("jt-clickable-block", ClickableBlockComponent);
}
